import {
  STORAGE_ITEM_STORE_CACHED_VIRTUALIZER,
  STORAGE_ITEM_STORE_OFFER_VIEWS,
} from '@wwe-portal/web/utils/constants/keys';
import { addDDAction, addDDError } from '../analytics/datadog';

const isQuotaExceededError = (e: unknown): boolean => {
  return (
    e instanceof DOMException &&
    // everything except Firefox
    (e.name === 'QuotaExceededError' ||
      // Firefox
      e.name === 'NS_ERROR_DOM_QUOTA_REACHED' ||
      e.code === 22 ||
      e.code === 1014)
  );
};

/**
 * Default quota recovery: drops the two largest, purely cosmetic localStorage
 * blobs — the offer-view "new" badge history and the scroll-measurement cache.
 * Both are disposable by design (losing them only re-shows "new" badges and
 * resets scroll caching), so freeing them is always a safe way to make room for
 * any write. Because this is the default recovery for `safeSetLocalStorage`,
 * any guarded write that hits quota reclaims this space and retries.
 */
export const evictCosmeticStorage = () => {
  localStorage.removeItem(STORAGE_ITEM_STORE_OFFER_VIEWS);
  localStorage.removeItem(STORAGE_ITEM_STORE_CACHED_VIRTUALIZER);
};

/**
 * Writes to localStorage without ever throwing. A full quota throws
 * `QuotaExceededError` synchronously from `setItem`; left unhandled it bubbles
 * into the React render and trips the error boundary ("Oops, Something Went
 * Wrong"). This wrapper swallows that failure so a write can never crash the
 * page.
 *
 * On quota it runs `onQuotaExceeded` (by default `evictCosmeticStorage`) to
 * free space, then retries once. `getValue` is a thunk so the retry
 * re-serializes after recovery has run.
 *
 * Returns `true` if the value was persisted, `false` otherwise.
 */
export const safeSetLocalStorage = (
  key: string,
  getValue: () => string,
  onQuotaExceeded: () => void = evictCosmeticStorage
): boolean => {
  try {
    localStorage.setItem(key, getValue());
    return true;
  } catch (e) {
    if (isQuotaExceededError(e)) {
      try {
        onQuotaExceeded();
        localStorage.setItem(key, getValue());
        // Recovery worked: the user hit the quota ceiling but we freed space
        // and the write went through. Tracked as an action (not an error) so we
        // can measure how often eviction rescues users without polluting RUM
        // errors with handled events.
        addDDAction('localStorage_quota_recovered', { key });
        return true;
      } catch (retryError) {
        addDDError(retryError, { key, reason: 'safeSetLocalStorage retry' });
        return false;
      }
    }
    addDDError(e, { key, reason: 'safeSetLocalStorage' });
    return false;
  }
};
